import { useMemo, useRef, type CSSProperties } from 'react';
import { BinPicker, type PickableBin } from './BinSelect';
import type { ReceiptLine } from './ItemLines';
import { SearchSelect, type SearchOption } from './SearchSelect';
import { StorePicker, type PickableStore } from './StorePicker';
import {
  newSupplierName,
  newSupplierValue,
  sameAsAbove,
  type DeliveryField,
} from './receiptDelivery';

/** What a stock-in's lines can choose their delivery details from. */
export interface DeliveryOptions {
  suppliers: ReadonlyArray<{ id: string; code: string; nameEn: string }>;
  /** Offers "+ Add … as a new supplier" for a name that matches none. */
  canCreateSupplier: boolean;
  stores: ReadonlyArray<PickableStore>;
  /** Each store's active bins; a store still loading is missing. */
  binsByStore: ReadonlyMap<string, ReadonlyArray<PickableBin>>;
  /** Item, quantity and cost may be read-only while these still change. */
  locked?: boolean | undefined;
}

/** What changing a field on a line can hand on to the lines below it. */
export interface ApplyOffer {
  key: string;
  field: DeliveryField;
  keys: string[];
}

const FIELD_NAMES: Record<DeliveryField, string> = {
  supplier: 'supplier',
  store: 'store',
  bin: 'bin',
  vehicleNo: 'vehicle',
  invoiceRef: 'invoice',
  remarks: 'remarks',
};

export const VEHICLE_LIST_ID = 'itemlines-vehicles';

/**
 * A stock-in line's second row: who supplied it, where it went, on which
 * vehicle and invoice. Filled from the line above when the line is started; a value that only repeats the line above is drawn quieter, so the
 * line where the supplier or the truck changes stands out.
 */
export function DeliveryStrip({
  lines,
  index,
  options,
  showBin,
  offer,
  onChange,
  onApply,
  onDismiss,
}: {
  lines: readonly ReceiptLine[];
  index: number;
  options: DeliveryOptions;
  /** Whether any line's store has bins, so every strip keeps the same cells. */
  showBin: boolean;
  offer: ApplyOffer | null;
  /** A change to this line; `oldValue` is what `field` held before it, so the
   *  lines below that still show it can be offered the new one. */
  onChange: (patch: Partial<ReceiptLine>, field: DeliveryField, oldValue: string, offerNow: boolean) => void;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const line = lines[index]!;
  const n = index + 1;
  const locked = options.locked ?? false;
  const bins = options.binsByStore.get(line.storeId) ?? [];
  // A text box hands its change on once the receiver leaves it, not on every
  // keystroke: what it held when focused is what the lines below still show.
  const focusedValue = useRef<string>('');

  const supplierOptions = useMemo(() => {
    const known: SearchOption[] = options.suppliers.map((s) => ({ value: s.id, label: s.nameEn, meta: s.code }));
    const typed = [...new Set(lines.map((l) => l.supplierId).filter((id) => newSupplierName(id) !== null))];
    return [...known, ...typed.map((value) => ({ value, label: newSupplierName(value)!, meta: 'New' }))];
  }, [options.suppliers, lines]);

  const stores = useMemo(() => [...options.stores], [options.stores]);

  function pickSupplier(option: SearchOption | null) {
    onChange({ supplierId: option?.value ?? '', supplierLabel: option?.label ?? '' }, 'supplier', line.supplierId, true);
  }

  /** A typed name matching a supplier on file (or one already typed on
   *  another line) is that supplier, not a second one with the same name. */
  function createSupplier(name: string) {
    const wanted = name.trim();
    if (!wanted) return;
    const same = (other: string) => other.trim().toLowerCase() === wanted.toLowerCase();
    const onFile = options.suppliers.find((s) => same(s.nameEn));
    if (onFile) {
      pickSupplier({ value: onFile.id, label: onFile.nameEn });
      return;
    }
    const typed = supplierOptions.find((o) => newSupplierName(o.value) !== null && same(o.label));
    pickSupplier(typed ?? { value: newSupplierValue(wanted), label: wanted });
  }

  const cols = [
    'minmax(10rem, 1.5fr)',
    'minmax(15rem, 2.2fr)',
    ...(showBin ? ['minmax(7rem, 1fr)'] : []),
    'minmax(7rem, 1fr)',
    'minmax(7rem, 1fr)',
    'minmax(8rem, 1.3fr)',
  ].join(' ');

  const cell = (field: DeliveryField) =>
    `itemlines__dcell${sameAsAbove(lines, index, field) ? ' itemlines__dcell--same' : ''}`;
  const sameTitle = (field: DeliveryField) => (sameAsAbove(lines, index, field) ? 'Same as the line above' : undefined);

  function textField(field: 'vehicleNo' | 'invoiceRef' | 'remarks', label: string, extra: object = {}) {
    return (
      <label className={cell(field)} title={sameTitle(field)}>
        <span className="itemlines__dlabel">
          {label}
          <span className="sr-only"> for item {n}</span>
        </span>
        <input
          className={`field__input${field === 'invoiceRef' || field === 'vehicleNo' ? ' field__input--mono' : ''}`}
          value={line[field]}
          disabled={locked}
          onFocus={() => {
            focusedValue.current = line[field];
          }}
          onChange={(e) => onChange({ [field]: e.target.value }, field, line[field], false)}
          onBlur={() => {
            if (line[field] !== focusedValue.current) onChange({}, field, focusedValue.current, true);
          }}
          {...extra}
        />
      </label>
    );
  }

  return (
    <div className="itemlines__delivery" style={{ '--delivery-cols': cols } as CSSProperties}>
      <div className={cell('supplier')} title={sameTitle('supplier')}>
        <SearchSelect
          label={`Supplier for item ${n}`}
          visibleLabel="Supplier"
          labelClassName="itemlines__dlabel"
          required
          disabled={locked}
          value={line.supplierId}
          selectedLabel={line.supplierLabel || undefined}
          options={supplierOptions}
          placeholder="Search suppliers…"
          commitExact
          onChange={pickSupplier}
          {...(options.canCreateSupplier
            ? { onCreate: createSupplier, createLabel: (name: string) => `+ Add “${name}” as a new supplier` }
            : {})}
        />
        {newSupplierName(line.supplierId) !== null && (
          <span className="itemlines__hint">New — registered when saved</span>
        )}
      </div>

      <div className={`${cell('store')} itemlines__dcell--store`} title={sameTitle('store')}>
        {/* Code and name, each searchable, as on every other store field. */}
        <StorePicker
          label={`Store for item ${n}`}
          visibleLabels={{ code: 'Store code', name: 'Store name' }}
          labelClassName="itemlines__dlabel"
          required
          disabled={locked}
          value={line.storeId}
          stores={stores}
          onChange={(storeId) => {
            if (storeId === line.storeId) return;
            // A bin belongs to one store.
            onChange({ storeId, binId: '', binLabel: '' }, 'store', line.storeId, true);
          }}
        />
      </div>

      {showBin && (
        <div className={cell('bin')} title={sameTitle('bin')}>
          {bins.length > 0 || line.binId ? (
            <>
              <BinPicker
                bins={bins}
                label={`Bin for item ${n}`}
                visibleLabel="Bin"
                labelClassName="itemlines__dlabel"
                placeholder="Loose"
                disabled={locked}
                value={line.binId}
                selectedLabel={line.binLabel || undefined}
                onChange={(binId) => {
                  const bin = bins.find((b) => b.id === binId);
                  onChange({ binId, binLabel: bin ? `${bin.code} — ${bin.nameEn}` : '' }, 'bin', line.binId, true);
                }}
              />
              {line.binId && options.binsByStore.has(line.storeId) && !bins.some((b) => b.id === line.binId) && (
                <span className="itemlines__hint itemlines__hint--warn">Deleted — choose another bin</span>
              )}
            </>
          ) : (
            <>
              <span className="itemlines__dlabel">Bin</span>
              <span className="itemlines__na" title="This store has no bins">
                —
              </span>
            </>
          )}
        </div>
      )}

      {textField('vehicleNo', 'Vehicle', { list: VEHICLE_LIST_ID, autoComplete: 'off', maxLength: 32 })}
      {textField('invoiceRef', 'Invoice', { maxLength: 64 })}
      {textField('remarks', 'Remarks')}

      {offer && offer.key === line.key && (
        <div className="itemlines__offer" role="status">
          <button type="button" className="btn btn--sm" onClick={onApply}>
            Apply this {FIELD_NAMES[offer.field]} to the {offer.keys.length === 1 ? 'line' : `${offer.keys.length} lines`}{' '}
            below
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onDismiss} aria-label="Keep the lines below as they are">
            Keep them
          </button>
        </div>
      )}
    </div>
  );
}
